import { CalendarPlus, Save, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { inputCls } from "@/components/FormBits";
import { api, formatApiError } from "@/lib/api";

export const GoalTitleModal = ({ goal, onClose, onSaved }) => {
  const [title, setTitle] = useState(goal.title);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch(`/goals/${goal.id}/title`, { title });
      onSaved(data);
      toast.success("Nome da meta atualizado.");
      onClose();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="modal-edit-goal-name" role="dialog" aria-modal="true" className="savings-backdrop">
      <section className="savings-modal">
        <button type="button" onClick={onClose} data-testid="close-edit-goal-name" className="modal-close-button" aria-label="Fechar edição"><X className="h-6 w-6" /></button>
        <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Editar nome</p>
        <h2 className="mt-2 font-heading text-3xl font-extrabold text-emerald-950">Como você quer chamar esta meta?</h2>
        <input data-testid="input-edit-goal-name" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160} className={`mt-6 ${inputCls}`} autoFocus />
        {error && <p role="alert" className="mt-4 rounded-xl border-2 border-red-200 bg-red-50 p-4 text-red-800">{error}</p>}
        <button type="button" onClick={save} disabled={saving} data-testid="btn-save-goal-name" className="savings-confirm-button mt-6 disabled:opacity-60"><Save className="h-5 w-5" />{saving ? "Salvando..." : "Salvar nome"}</button>
      </section>
    </div>
  );
};

export const GoalDeleteModal = ({ goal, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const remove = async () => {
    setDeleting(true);
    setError("");
    try {
      await api.delete(`/goals/${goal.id}`);
      onDeleted(goal.id);
      toast.success("Meta excluída.");
      onClose();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="modal-confirm-delete" role="dialog" aria-modal="true" className="savings-backdrop">
      <section className="savings-modal">
        <button type="button" onClick={onClose} data-testid="close-delete-goal" className="modal-close-button" aria-label="Fechar exclusão"><X className="h-6 w-6" /></button>
        <p className="text-sm font-bold uppercase tracking-wide text-red-700">Ação permanente</p>
        <h2 className="mt-2 font-heading text-3xl font-extrabold text-emerald-950">Excluir “{goal.title}”?</h2>
        <p className="mt-4 text-lg leading-relaxed text-slate-700">A meta e todo o histórico dela serão excluídos. Esta ação não pode ser desfeita.</p>
        {error && <p role="alert" className="mt-4 rounded-xl border-2 border-red-200 bg-red-50 p-4 text-red-800">{error}</p>}
        <div className="mt-7 grid gap-3 sm:grid-cols-2">
          <button type="button" onClick={onClose} data-testid="btn-cancel-delete" className="min-h-[54px] rounded-xl border-2 border-slate-300 bg-white px-4 text-lg font-bold text-slate-800 hover:bg-slate-50">Cancelar</button>
          <button type="button" onClick={remove} disabled={deleting} data-testid="btn-confirm-delete" className="inline-flex min-h-[54px] items-center justify-center gap-2 rounded-xl bg-red-700 px-4 text-lg font-bold text-white hover:bg-red-800 disabled:opacity-60"><Trash2 className="h-5 w-5" />{deleting ? "Excluindo..." : "Excluir meta"}</button>
        </div>
      </section>
    </div>
  );
};

export const GoalRepeatModal = ({ goal, onClose, onScheduled }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const repeat = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post(`/goals/${goal.id}/repeat-next-month`);
      onScheduled(data.goal, data.already_scheduled);
      onClose();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="modal-repeat-goal" role="dialog" aria-modal="true" className="savings-backdrop">
      <section className="savings-modal">
        <button type="button" onClick={onClose} data-testid="close-repeat-goal" className="modal-close-button" aria-label="Fechar repetição"><X className="h-6 w-6" /></button>
        <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Próximo mês</p>
        <h2 className="mt-2 font-heading text-3xl font-extrabold text-emerald-950">Repetir esta meta?</h2>
        <p className="mt-4 text-lg leading-relaxed text-slate-700">Uma nova meta igual ficará pronta para o primeiro dia do próximo mês, sem copiar depósitos ou progresso.</p>
        {error && <p role="alert" className="mt-4 rounded-xl border-2 border-red-200 bg-red-50 p-4 text-red-800">{error}</p>}
        <div className="mt-7 grid gap-3 sm:grid-cols-2">
          <button type="button" onClick={onClose} data-testid="btn-cancel-repeat-next-month" className="min-h-[54px] rounded-xl border-2 border-slate-300 bg-white px-4 text-lg font-bold text-slate-800 hover:bg-slate-50">Agora não</button>
          <button type="button" onClick={repeat} disabled={saving} data-testid="btn-confirm-repeat-next-month" className="inline-flex min-h-[54px] items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 text-lg font-bold text-white hover:bg-emerald-800 disabled:opacity-60"><CalendarPlus className="h-5 w-5" />{saving ? "Preparando..." : "Repetir no próximo mês"}</button>
        </div>
      </section>
    </div>
  );
};